/**
 * 404 Not Found page.
 *
 * Mounted via App.js as the catch-all route. Keeps brand consistency with the
 * public site (#F7F5F0 / #2A3324) and routes folks back to safety.
 */
import { Link } from "react-router-dom";
import { Home, ArrowRight, MessageCircle } from "lucide-react";

export default function NotFound() {
  return (
    <div
      data-testid="not-found-page"
      className="min-h-screen bg-[#F7F5F0] text-[#2A3324] flex items-center justify-center px-6 py-16 font-sans"
    >
      <div className="max-w-2xl w-full">
        {/* Asymmetric layout — 404 anchored bottom-left, copy off-center */}
        <div className="relative">
          <div className="font-serif text-[120px] sm:text-[180px] lg:text-[220px] leading-none font-light tracking-tighter text-[#2A3324]/15 select-none">
            404
          </div>
          <div className="-mt-6 sm:-mt-10 lg:-mt-16 ml-1 max-w-md">
            <p className="uppercase tracking-[0.3em] text-[10px] sm:text-xs text-[#5C715E] font-semibold mb-3">
              page not found
            </p>
            <h1 className="font-serif text-3xl sm:text-4xl font-light leading-tight mb-4">
              We can't find that page.
            </h1>
            <p className="text-[#2A3324]/70 leading-relaxed mb-8">
              It may have been moved, renamed, or never existed. If a doctor or
              staff member sent you a link, message them so they can resend the
              correct one.
            </p>

            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/"
                data-testid="not-found-home-link"
                className="inline-flex items-center justify-center gap-2 bg-[#2A3324] text-[#F7F5F0] px-6 py-3 rounded-full text-sm font-medium hover:bg-[#5C715E] transition-colors"
              >
                <Home size={16} />
                Back to home
              </Link>
              <Link
                to="/contact"
                data-testid="not-found-contact-link"
                className="inline-flex items-center justify-center gap-2 bg-transparent text-[#2A3324] px-6 py-3 rounded-full text-sm font-medium border border-[#2A3324]/20 hover:border-[#2A3324]/50 transition-colors"
              >
                <MessageCircle size={16} />
                Contact support
              </Link>
            </div>

            <div className="mt-10 pt-6 border-t border-[#2A3324]/10">
              <p className="text-xs uppercase tracking-widest text-[#2A3324]/50 font-semibold mb-3">
                Popular pages
              </p>
              <ul className="space-y-1.5 text-sm">
                {[
                  { label: "Take the quiz", href: "/#quiz" },
                  { label: "See pricing", href: "/pricing" },
                  { label: "Browse peptides", href: "/peptides/weight-management" },
                  { label: "Patient portal login", href: "/admin/login" },
                ].map((item) => (
                  <li key={item.href}>
                    <Link
                      to={item.href}
                      className="group inline-flex items-center gap-2 text-[#5C715E] hover:text-[#2A3324] transition-colors"
                    >
                      {item.label}
                      <ArrowRight
                        size={12}
                        className="opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all"
                      />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
